/**
 * InboxDirectList — DM conversation list tab.
 * Reuses existing conversation data with navigation to inbox chat.
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { uz } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@turnifly/shared/stores'
import { useConversations } from '@turnifly/shared/hooks'
import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'

export const InboxDirectList: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { data: conversations, isLoading } = useConversations(user?.id)

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  const directConvs = (conversations ?? []).filter((c) => c.type === 'direct')

  if (directConvs.length === 0) {
    return (
      <div className="py-12 text-center text-text-hint text-sm">
        {t('inbox.noDirect')}
      </div>
    )
  }

  return (
    <div>
      {directConvs.map((conv) => {
        const badge = getBadgeDisplay(false, conv.participant?.badge_type)
        const timeAgo = conv.lastMessage?.created_at
          ? formatDistanceToNow(new Date(conv.lastMessage.created_at), {
              addSuffix: false,
              locale: i18n.language === 'uz' ? uz : undefined,
            })
          : ''

        const isMySender = conv.lastMessage?.sender_id === user?.id
        const prefix = isMySender ? `${t('dm.you')}: ` : ''
        const hasMedia = (conv.lastMessage?.media_urls?.length ?? 0) > 0
        const textPart = conv.lastMessage?.text ?? (hasMedia ? `📷 ${t('dm.photo')}` : '')
        const previewText = conv.lastMessage ? `${prefix}${textPart}` : ''

        return (
          <button
            key={conv.id}
            onClick={() => navigate(`/inbox/chat/${conv.id}`, {
              state: { participantName: conv.participant?.full_name ?? '' },
            })}
            className="flex items-center gap-3 w-full px-4 py-3 hover:bg-white/[0.02]
              transition-colors tap-highlight-transparent"
          >
            <AvatarWithBadge
              src={conv.participant?.avatar_url}
              fallback={(conv.participant?.full_name ?? '?').charAt(0)}
              size="md"
              badge={badge}
            />
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-text truncate">
                  {conv.participant?.full_name ?? t('dm.unknown')}
                </span>
                {timeAgo && (
                  <span className="text-[11px] text-text-hint/60 shrink-0 ml-2">{timeAgo}</span>
                )}
              </div>
              {previewText && (
                <p className="text-xs text-text-hint mt-0.5 truncate">{previewText}</p>
              )}
            </div>
            {conv.unreadCount > 0 && (
              <div className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-primary px-1.5 shrink-0">
                <span className="text-[10px] font-bold text-white">
                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                </span>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}
